import React, { useState } from 'react';
import { FaEye, FaEyeSlash, FaGripVertical } from "react-icons/fa";
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { LayerControlData } from '../../types';
import styles from './LayerToggle.module.css';

interface LayerToggleProps {
  layer: LayerControlData;
  isActive: boolean;
  onToggle: (layerId: string) => void;
  onVisibilityToggle: (layerId: string) => void;
  onControlChange: (layerId: string, controlType: string, value: string | number) => void;
}

const LayerToggle: React.FC<LayerToggleProps> = ({
  layer,
  isActive,
  onToggle,
  onVisibilityToggle,
  onControlChange
}) => {
  const [localTransparency, setLocalTransparency] = useState(layer.transparency);
  
  // Sync local transparency with layer prop changes
  React.useEffect(() => {
    setLocalTransparency(layer.transparency);
  }, [layer.transparency]);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: layer.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
  };

  const handleToggle = (e: React.MouseEvent) => {
    // Don't toggle if clicking on dropdown controls, visibility button, or drag handle
    if ((e.target as HTMLElement).closest(`.${styles.layerControls}`) || 
        (e.target as HTMLElement).closest(`.${styles.visibilityButton}`) ||
        (e.target as HTMLElement).closest(`.${styles.dragHandle}`)) {
      return;
    }
    onToggle(layer.id);
  };

  const handleVisibilityToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    onVisibilityToggle(layer.id);
  };

  const handleTransparencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    setLocalTransparency(value);
    onControlChange(layer.id, 'transparency', value);
  };

  const handleFilterChange = (filterType: string, value: string) => {
    onControlChange(layer.id, filterType, value);
  };

  const renderLegend = () => {
    const legendData: { [key: string]: { label: string; color: string; }[] } = {
      'heatIslands': [
        { label: 'High Priority (+7.2°F)', color: '#DC2626' },
        { label: 'Medium Priority (+3.6°F)', color: '#F59E0B' }
      ],
      'floodZones': [
        { label: 'Zone AE (High Risk)', color: '#3B82F6' },
        { label: 'Zone X (Moderate Risk)', color: '#1E40AF' },
        { label: 'Other Zones', color: '#93C5FD' }
      ],
      'environmentalJustice': [
        { label: 'High (>7.0)', color: '#4C1D95' },
        { label: 'Medium (6.55-7.0)', color: '#9333EA' },
        { label: 'Low (6.4-6.55)', color: '#A855F7' }
      ]
    };

    const legend = legendData[layer.id];
    if (!legend) return null;

    return (
      <div className={styles.controlGroup}>
        <label className={styles.controlLabel}>Legend</label>
        <div className={styles.legend}>
          {legend.map((item, index) => (
            <div key={index} className={styles.legendItem}>
              <div 
                className={styles.legendColor} 
                style={{ backgroundColor: item.color }}
              />
              <span className={styles.legendLabel}>{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderFilterControls = () => {
    const filterControls: { [key: string]: { label: string; options: string[] } } = {
      'heatIslands': {
        label: 'Temperature Range',
        options: [
          'All temperatures',
          'Medium priority (+3.6°F)',
          'High priority (+7.2°F)'
        ]
      },
      'floodZones': {
        label: 'Zone Types',
        options: [
          'All flood zones',
          'Zone AE (High risk)',
          'Zone X (Moderate risk)',
          'Zone A (Undetermined)'
        ]
      },
      'environmentalJustice': {
        label: 'EJ Score Range',
        options: [
          'All EJ scores',
          'Low (6.4-6.55)',
          'Medium (6.55-7.0)',
          'High (>7.0)'
        ]
      },
    };

    const layerFilters = filterControls[layer.id];
    if (!layerFilters) return null;

    return (
      <div className={styles.controlGroup}>
        <label className={styles.controlLabel}>{layerFilters.label}</label>
        <select
          className={styles.filterDropdown}
          value={layer.filters.primary || layerFilters.options[0]}
          onChange={(e) => handleFilterChange('primary', e.target.value)}
        >
          {layerFilters.options.map((option, index) => (
            <option key={index} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
    );
  };


  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`${styles.layerToggle} ${isActive ? styles.active : ''} ${isDragging ? styles.dragging : ''}`} 
      onClick={handleToggle}
    >
      <div className={styles.layerMain}>
        <div
          className={styles.dragHandle}
          {...attributes}
          {...listeners}
          title="Drag to reorder layers"
        >
          <FaGripVertical />
        </div>
        <div className={styles.layerIcon}>{layer.icon}</div>
        <div className={styles.layerContent}>
          <div className={styles.layerName}>{layer.name}</div>
          <div className={styles.layerDetails}>{layer.description}</div>
        </div>
        <button
          className={`${styles.visibilityButton} ${!layer.isVisible ? styles.visibilityButtonHidden : ''}`}
          onClick={handleVisibilityToggle}
          title={layer.isVisible ? 'Hide layer' : 'Show layer'}
        >
          {layer.isVisible ? <FaEye /> : <FaEyeSlash />}
        </button>
      </div>
      
      {isActive && (
        <div className={styles.layerControls} onClick={(e) => e.stopPropagation()}>
          <div className={styles.controlGroup}>
            <label className={styles.controlLabel}>Transparency</label>
            <input
              type="range"
              className={styles.transparencySlider}
              min="0"
              max="100"
              value={localTransparency}
              onChange={handleTransparencyChange}
            />
            <div className={styles.transparencyValue}>{localTransparency}%</div>
          </div>
          
          {renderFilterControls()}
          {renderLegend()}
          {/* {renderSecondaryFilter()} */}
        </div>
      )}
    </div>
  );
};

export default LayerToggle;